import { defineStore } from 'pinia';

import { Config } from '@/config';
import { Constants } from '@/constants';
import { i18n } from '@/plugins/i18n';
import apiService from '@/services/api.service';
import type { MailingListDto } from '@/services/types/api.types';

export interface MailingListState {
  mailingLists: MailingListDto[];
  isLoading: boolean;
  itemLoadingStates: Set<number>;
}

const { t } = i18n.global;

function getTypes(): any[] {
  return [
    { label: t('campaign.mailingLists.types.subscriber'), value: Constants.MailingList.type.subscriber },
    { label: t('campaign.mailingLists.types.participant'), value: Constants.MailingList.type.participant },
  ];
}

function getParentsTypes(): any[] {
  return [
    { label: t('campaign.mailingLists.parent.community'), value: Constants.MailingList.Parent.community },
    { label: t('campaign.mailingLists.parent.consultation'), value: Constants.MailingList.Parent.consultation },
    { label: t('campaign.mailingLists.parent.project'), value: Constants.MailingList.Parent.project },
  ];
}

function getMailingListOptions(): any[] {
  return [
    {
      label: `${t('campaign.mailingLists.parent.community')} - ${t('campaign.mailingLists.types.subscriber')}`,
      value: Constants.MailingList.DropdownType.CommunitySubscribers,
    },
    {
      label: `${t('campaign.mailingLists.parent.consultation')} - ${t('campaign.mailingLists.types.participant')}`,
      value: Constants.MailingList.DropdownType.ConsultationParticipants,
    },
    {
      label: `${t('campaign.mailingLists.parent.consultation')} - ${t('campaign.mailingLists.types.subscriber')}`,
      value: Constants.MailingList.DropdownType.ConsultationSubscribers,
    },
    {
      label: `${t('campaign.mailingLists.parent.project')} - ${t('campaign.mailingLists.types.subscriber')}`,
      value: Constants.MailingList.DropdownType.ProjectSubscribers,
    },
  ];
}

export const useMailingListStore = defineStore({
  id: 'mailingLists',
  state: (): MailingListState => ({
    mailingLists: [],
    isLoading: false,
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),
    getItem:
      (state) =>
      (id: number): MailingListDto | undefined =>
        state.mailingLists.find((c) => c.id === id),
    getMailingListCategory: (state) => (parent: number, type: number) => {
      const parentItem = getParentsTypes().find((t) => t.value === parent);
      const typeItem = getTypes().find((t) => t.value === type);
      return `${parentItem.label} - ${typeItem.label}`;
    },
    getMailingListOptions: (state) => getMailingListOptions,
  },
  actions: {
    async fetchMailingLists() {
      this.isLoading = true;
      try {
        const { data } = await apiService.get<MailingListDto[]>(`${Config.api.v2}/mailing-lists`);
        this.mailingLists = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchMailingListByParent(parent: number, parentId?: number, type?: number) {
      this.isLoading = true;
      try {
        const params = { parent, parentId, type };
        const { data } = await apiService.get<MailingListDto[]>(`${Config.api.v2}/mailing-lists`, params);
        return Array.isArray(data) && data.length > 0 ? data[0] : null;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
